import { useQuery } from '@apollo/client'
import { Nullable } from '@dai/common'
import { ChevronLeft } from '@mui/icons-material'
import {
  Box,
  Button,
  CircularProgress,
  Container,
  Grid2 as Grid,
  Typography,
} from '@mui/material'
import { useNavigate } from 'react-router-dom'

import {
  ACTIVE_SUBSCRIPTION,
  ActiveSubscriptionQuery,
  ActiveSubscriptionQueryVariables,
  PlanType,
  SubscriptionTypeFragment,
} from '@/graphql'
import useLoggedInUser from '@/hooks/useLoggedInUser'
import useT from '@/i18n/useT'

import PlanPricingTile from './PlanPricingTile'

type PricingTableProps = {
  viewOnly?: boolean
  handleSelectPlan: (plan: SubscriptionTypeFragment) => void
  selectedPlan: Nullable<string>
  plans: SubscriptionTypeFragment[]
  loading: boolean
  error: any
  checkoutInfo: string
}

function PricingTable({
  viewOnly = false,
  handleSelectPlan,
  selectedPlan,
  plans,
  loading,
  error,
  checkoutInfo,
}: PricingTableProps) {
  const { t } = useT()
  const { user } = useLoggedInUser()
  const navigate = useNavigate()

  const {
    data,
    loading: queryLoading,
    error: queryError,
  } = useQuery<ActiveSubscriptionQuery, ActiveSubscriptionQueryVariables>(
    ACTIVE_SUBSCRIPTION,
    {
      skip: !user,
    },
  )

  if (queryLoading || loading) {
    return (
      <Box
        sx={{
          display: 'flex',
          justifyContent: 'center',
          alignItems: 'center',
        }}
      >
        <CircularProgress />
      </Box>
    )
  }

  if (queryError || error) {
    return (
      <Box sx={{ p: 2, textAlign: 'center' }}>
        <Typography color="error">
          {t('pricingTable.errorLoadingPlans')}
        </Typography>
        <Typography color="error">{(queryError || error).message}</Typography>
      </Box>
    )
  }

  const sortedPlans = [...plans]
    .sort((a, b) => {
      if (a.name.toLowerCase().includes('enterprise')) return 1
      if (b.name.toLowerCase().includes('enterprise')) return -1

      const aPrice = parseFloat(a.prices[0]?.unitAmount || '0')
      const bPrice = parseFloat(b.prices[0]?.unitAmount || '0')
      return aPrice - bPrice
    })
    .filter(plan =>
      data?.activeSubscription?.planType === PlanType.PREMIUM_FREE
        ? plan.planType !== PlanType.BASIC
        : true,
    )

  return (
    <Container>
      <Box>
        {user?.activeSubscription && window.history.length > 1 && (
          <Button
            variant="outlined"
            onClick={() => navigate(-1)}
            startIcon={<ChevronLeft />}
          >
            {t('pricingTable.back')}
          </Button>
        )}
        {!viewOnly && (
          <>
            <Typography
              variant="h4"
              align="center"
              gutterBottom
              fontWeight="medium"
            >
              {t('pricingTable.choosePlan')}
            </Typography>
            <Typography
              variant="body1"
              align="center"
              color="text.secondary"
              sx={{ mb: 4 }}
            >
              {checkoutInfo}
            </Typography>
          </>
        )}
        <Grid container spacing={3} justifyContent="center">
          {sortedPlans.map(plan => (
            <Grid size={{ xs: 12, md: 4 }} key={plan.id}>
              <PlanPricingTile
                plan={plan}
                currentPlanType={data?.activeSubscription?.planType}
                nextPlanType={data?.activeSubscription?.nextPlanType}
                selected={selectedPlan === plan.id}
                handleSelectPlan={handleSelectPlan}
                viewOnly={viewOnly}
              />
            </Grid>
          ))}
        </Grid>
      </Box>
    </Container>
  )
}

export default PricingTable
